import React, { useEffect, useState } from 'react';
import { Modal, Button, Form, Alert, Spinner } from 'react-bootstrap';
import { getInstanceName, setInstanceName } from '../../api/instanceNameApi';

const EXAMPLE_NAMES = ['Utveckling', 'Demo'];

interface InstanceNameModalProps {
    show: boolean;
    /** didSave = the name was written; caller should refresh */
    onClose: (didSave: boolean) => void;
}

/**
 * Sets `global/instanceName` — the customer name shown in the
 * navigation profile menu and stamped on configuration exports.
 */
const InstanceNameModal: React.FC<InstanceNameModalProps> = ({ show, onClose }) => {
    const [name, setName] = useState('');
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        if (!show) return;
        setError(null);
        setLoading(true);
        getInstanceName()
            .then(current => setName(current ?? ''))
            .catch(() => setName(''))
            .finally(() => setLoading(false));
    }, [show]);

    const handleClose = () => {
        if (saving) return;
        onClose(false);
    };

    const handleSave = async () => {
        setSaving(true);
        setError(null);
        try {
            await setInstanceName(name);
            onClose(true);
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Kunde inte spara instansnamnet. Försök igen.');
        } finally {
            setSaving(false);
        }
    };

    return (
        <Modal show={show} onHide={handleClose} backdrop="static">
            <Modal.Header closeButton={!saving}>
                <Modal.Title>Instansnamn</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <p className="text-muted">
                    Kundnamnet för instansen. Visas i navigeringsmenyn och anger
                    avsändare vid konfigurationsexport.
                </p>
                {error && <Alert variant="danger">{error}</Alert>}
                {loading ? (
                    <div className="text-center py-3">
                        <Spinner animation="border" size="sm" />
                    </div>
                ) : (
                    <Form onSubmit={e => { e.preventDefault(); handleSave(); }}>
                        <Form.Group>
                            <Form.Label>Instansnamn</Form.Label>
                            <Form.Control
                                type="text"
                                value={name}
                                autoFocus
                                disabled={saving}
                                onChange={e => setName(e.target.value)}
                                placeholder="T.ex. Utveckling"
                            />
                        </Form.Group>
                        <div className="d-flex align-items-center gap-2 mt-2">
                            <span className="text-muted small">Exempel:</span>
                            {EXAMPLE_NAMES.map(example => (
                                <Button
                                    key={example}
                                    variant="outline-secondary"
                                    size="sm"
                                    disabled={saving}
                                    onClick={() => setName(example)}
                                >
                                    {example}
                                </Button>
                            ))}
                        </div>
                    </Form>
                )}
            </Modal.Body>
            <Modal.Footer>
                <Button variant="outline-secondary" onClick={handleClose} disabled={saving}>
                    Avbryt
                </Button>
                <Button
                    variant="primary"
                    onClick={handleSave}
                    disabled={loading || saving || !name.trim()}
                >
                    {saving ? (
                        <>
                            <Spinner size="sm" className="me-2" />
                            Sparar...
                        </>
                    ) : (
                        'Spara'
                    )}
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

export default InstanceNameModal;
